import { useState, useEffect, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FlaskConical,
  Search,
  Plus,
  Trash2,
  Upload,
  Play,
  Download,
  FileText,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Loader2,
  Clock,
  FileJson,
  AlertTriangle,
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Settings2,
  Square,
  FolderSync,
  Folder,
  List,
  LayoutGrid,
  Import,
} from 'lucide-react'
import {
  apiClient,
  SdlRun,
  SdlJob,
  SdlStageStatus,
  SdlRunsListResponse,
  SdlStageResult,
  SdlSotSyncFile,
  SOTFileEntry,
} from '../../lib/api'
import ConfirmationModal from '../../components/ConfirmationModal'
import LarsConfigEditor from '../../components/LarsConfigEditor'

// ─── Constants ────────────────────────────────────────────────────────────────

const STAGE_NAMES: Record<number, string> = {
  1: 'Validate Inputs',
  2: 'Entity Modeling',
  3: 'Variables & Strategic Foci',
  4: 'Expand Question Templates',
  5: 'Answer Questions',
  6: 'Extract Existing FAQs',
  7: 'Assemble Outputs',
}

const STAGE_DESCRIPTIONS: Record<number, string> = {
  1: 'Validates and normalises crawl, competitor and questionnaire inputs',
  2: 'Builds the multi-entity model from the validated inputs',
  3: 'Derives variables, competitors and strategic foci from the entity model',
  4: 'Expands question templates with the derived variables',
  5: 'Generates reference answers for every question',
  6: 'Extracts existing FAQs from the crawled site content',
  7: 'Assembles the final SDL, entity model export and upsert SQL',
}

const TOTAL_STAGES = 7
const POLL_INTERVAL_MS = 3000
const SDL_STALE_MS = 5 * 60 * 1000
const SDL_RUNS_CACHE_KEY = 'sdl_runs_cache'

// Input categories mirror the generator's inputs/ tree (see sdl/functions core/models.py)
// Categories whose destination filename is fixed because stage 1 loads that
// exact path. Keep this the single source of truth — both manual upload and
// SOT sync read from it so they can't drift.
const REQUIRED_INPUT_FILENAME: Record<string, string> = {
  audit: 'MARKETING-AUDIT.md',
}

const INPUT_CATEGORIES: { key: string; label: string; hint: string; accept?: string }[] = [
  { key: 'crawl', label: 'Site Crawl', hint: 'Crawled site pages — .md files in name:/content: format', accept: '.md' },
  { key: 'competitors', label: 'Competitors', hint: 'Competitor crawls — .json arrays of {url, markdown}', accept: '.json' },
  { key: 'questionnaire', label: 'Questionnaire', hint: 'Client questionnaire / interview transcript(s) — plain .txt; multiple files are merged into one', accept: '.txt,.md,.text' },
  { key: 'audit', label: 'Marketing Audit', hint: 'One marketing-audit report — saved as MARKETING-AUDIT.md', accept: '.md,.txt' },
  { key: 'other', label: 'Other Docs', hint: 'Additional docs — .md files in name:/content: format', accept: '.md' },
  { key: 'extra', label: 'Extra (unformatted)', hint: 'Any additional unformatted text files' },
]

function readSdlRunsCache(): SdlRunsListResponse | undefined {
  try {
    const raw = sessionStorage.getItem(SDL_RUNS_CACHE_KEY)
    if (!raw) return undefined
    return JSON.parse(raw) as SdlRunsListResponse
  } catch {
    return undefined
  }
}

function writeSdlRunsCache(data: SdlRunsListResponse) {
  try {
    sessionStorage.setItem(SDL_RUNS_CACHE_KEY, JSON.stringify(data))
  } catch {
    // sessionStorage may be unavailable
  }
}

function clearSdlRunsCache() {
  try {
    sessionStorage.removeItem(SDL_RUNS_CACHE_KEY)
  } catch {
    // ignore
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface ActiveJob {
  jobId: string
  stageNumber: number
}

interface StageResultCache {
  [stageNumber: number]: SdlStageResult | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeTime(iso: string | null): string {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function formatAbsoluteTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
}

function formatBytes(size: number | null): string {
  if (size === null || size === undefined) return ''
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

// Default SOT-sync category by file extension.
function defaultCategoryFor(name: string): string {
  const lower = name.toLowerCase()
  if (lower.endsWith('.json')) return 'competitors'
  if (lower.endsWith('.md')) return 'other'
  return 'extra'
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function RunProgressBar({ completed, total }: { completed: number; total: number }) {
  const pct = total > 0 ? (completed / total) * 100 : 0
  const colour =
    completed === total ? 'bg-emerald-500' : completed > 0 ? 'bg-violet-500' : 'bg-gray-200'
  return (
    <div className="mt-1 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${colour}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}

function StageBadge({ status }: { status: 'not_started' | 'running' | 'completed' | 'failed' | 'cancelled' }) {
  const map = {
    not_started: { label: 'Not started', cls: 'bg-gray-100 text-gray-500', icon: Clock },
    running: { label: 'Running…', cls: 'bg-violet-100 text-violet-700', icon: Loader2 },
    completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    failed: { label: 'Failed', cls: 'bg-red-100 text-red-600', icon: XCircle },
    cancelled: { label: 'Cancelled', cls: 'bg-gray-100 text-gray-500', icon: Square },
  }
  const { label, cls, icon: Icon } = map[status]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
      {label}
    </span>
  )
}

// ─── Stage Card ───────────────────────────────────────────────────────────────

interface StageCardProps {
  stageNumber: number
  stageStatus: SdlStageStatus | undefined
  activeJob: ActiveJob | null
  result: SdlStageResult | null
  onRun: (stage: number, variant?: string) => void
  onCancel: () => void
  onDownloadLog: (stage: number) => void
  runLoading: boolean
  cancelLoading: boolean
  disableRun: boolean
  disableRunTitle?: string
  lastError?: string | null
}

function StageCard({
  stageNumber,
  stageStatus,
  activeJob,
  result,
  onRun,
  onCancel,
  onDownloadLog,
  runLoading,
  cancelLoading,
  disableRun,
  disableRunTitle,
  lastError,
}: StageCardProps) {
  const [expanded, setExpanded] = useState(false)

  const isRunning = activeJob?.stageNumber === stageNumber
  const isCompleted = stageStatus?.completed ?? false
  const isFailed = !isRunning && !isCompleted && !!lastError

  const stageJobStatus: 'not_started' | 'running' | 'completed' | 'failed' = isRunning
    ? 'running'
    : isCompleted
      ? 'completed'
      : isFailed
        ? 'failed'
        : 'not_started'

  const prevCompleted = useRef(isCompleted)
  useEffect(() => {
    if (!prevCompleted.current && isCompleted) setExpanded(true)
    prevCompleted.current = isCompleted
  }, [isCompleted])

  const summary = result?.summary ?? null
  const summaryKeys = summary ? Object.keys(summary).slice(0, 6) : []
  const zeroEntityWarning =
    stageNumber === 2 && summary && (summary.branch_count === 0 || summary.entity_count === 0)

  return (
    <div
      className={`rounded-xl border transition-all duration-200 ${isRunning
        ? 'border-violet-300 bg-violet-50/40 shadow-sm'
        : isCompleted
          ? 'border-emerald-200 bg-white'
          : 'border-gray-200 bg-white'
        }`}
    >
      <div className="flex items-center gap-3 px-5 py-4">
        <div
          className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-sm font-bold ${isCompleted
            ? 'bg-emerald-500 text-white'
            : isRunning
              ? 'bg-violet-500 text-white'
              : 'bg-gray-100 text-gray-500'
            }`}
        >
          {stageNumber}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">{STAGE_NAMES[stageNumber]}</p>
          <p className="text-xs text-gray-500 truncate">{STAGE_DESCRIPTIONS[stageNumber]}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StageBadge status={stageJobStatus} />
          {isCompleted && (
            <button
              onClick={() => setExpanded((p) => !p)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title={expanded ? 'Collapse' : 'Expand result'}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {isRunning && (
        <div className="px-5 pb-3">
          <div className="h-1.5 rounded-full bg-violet-100 overflow-hidden">
            <div className="h-full w-full rounded-full bg-violet-400 animate-pulse" />
          </div>
          <p className="mt-1.5 text-xs text-violet-600">Processing — polling for result…</p>
        </div>
      )}

      {isCompleted && stageStatus?.last_updated && !isRunning && (
        <div className="px-5 pb-3 -mt-1 flex items-center gap-1.5">
          <Clock className="h-3 w-3 text-gray-300 flex-shrink-0" />
          <p className="text-xs text-gray-400">
            Last run: <span className="text-gray-500 font-medium">{formatAbsoluteTime(stageStatus.last_updated)}</span>
            <span className="ml-1 text-gray-400">({formatRelativeTime(stageStatus.last_updated)})</span>
          </p>
        </div>
      )}

      {zeroEntityWarning && (
        <div className="mx-5 mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          The entity model parsed with zero entities/branches — check the stage log before continuing.
        </div>
      )}

      {expanded && isCompleted && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-3">
          {summary ? (
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs font-mono text-gray-700 overflow-auto max-h-48">
              {summaryKeys.map((k) => (
                <div key={k} className="flex gap-2">
                  <span className="text-violet-600 flex-shrink-0">"{k}":</span>
                  <span className="text-gray-600 truncate">
                    {typeof summary[k] === 'object' && summary[k] !== null
                      ? Array.isArray(summary[k])
                        ? `[${summary[k].length} items]`
                        : '{…}'
                      : JSON.stringify(summary[k])}
                  </span>
                </div>
              ))}
              {Object.keys(summary).length > 6 && (
                <p className="text-gray-400 mt-1">…and {Object.keys(summary).length - 6} more fields</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">Result summary not yet loaded.</p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 px-5 py-3 border-t border-gray-100">
        <button
          onClick={() => onRun(stageNumber)}
          disabled={isRunning || runLoading || disableRun}
          title={disableRun ? disableRunTitle : undefined}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Play className="h-3.5 w-3.5" />
          {isCompleted ? 'Re-run' : 'Run'}
        </button>
        {stageNumber === 6 && (
          <button
            onClick={() => onRun(stageNumber, 'b')}
            disabled={isRunning || runLoading || disableRun}
            title="Run the 6B variant — restricts extraction to documents containing 'faq'"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-violet-200 bg-white text-violet-600 hover:bg-violet-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Play className="h-3.5 w-3.5" />
            6B
          </button>
        )}
        {isRunning && (
          <button
            onClick={onCancel}
            disabled={cancelLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 bg-white text-red-600 hover:bg-red-50 disabled:opacity-40 transition-colors"
          >
            <Square className="h-3.5 w-3.5" />
            Cancel
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={() => onDownloadLog(stageNumber)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
          title="Download stage log"
        >
          <FileText className="h-3.5 w-3.5" />
          Log
        </button>
      </div>
    </div>
  )
}

// ─── SOT Sync Modal ───────────────────────────────────────────────────────────

interface SotSyncModalProps {
  subdomain: string
  isOpen: boolean
  isSyncing: boolean
  onClose: () => void
  onSync: (files: SdlSotSyncFile[]) => void
}

function SotSyncModal({ subdomain, isOpen, isSyncing, onClose, onSync }: SotSyncModalProps) {
  const [path, setPath] = useState('')
  const [selected, setSelected] = useState<Record<string, string>>({}) // path -> category

  const { data: listing, isLoading } = useQuery({
    queryKey: ['sdl-sot-files', subdomain, path],
    queryFn: () => apiClient.listSOTFiles(subdomain, path),
    enabled: isOpen,
    staleTime: 60 * 1000,
  })

  useEffect(() => {
    if (!isOpen) {
      setPath('')
      setSelected({})
    }
  }, [isOpen])

  if (!isOpen) return null

  const toggleFile = (file: SOTFileEntry) => {
    setSelected((prev) => {
      const next = { ...prev }
      if (next[file.path] !== undefined) {
        delete next[file.path]
      } else {
        next[file.path] = defaultCategoryFor(file.name)
      }
      return next
    })
  }

  const selectedCount = Object.keys(selected).length
  const crumbs = path ? path.split('/').filter(Boolean) : []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Sync from Source of Truth</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Copy files from <span className="font-medium">{subdomain}</span>'s SOT storage into this run's inputs
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg font-medium">✕</button>
        </div>

        <div className="px-6 py-2 border-b border-gray-100 flex items-center gap-1 text-xs text-gray-500 flex-wrap">
          <button onClick={() => setPath('')} className="hover:text-violet-600 font-medium">SOT root</button>
          {crumbs.map((c, i) => (
            <span key={i} className="flex items-center gap-1">
              <ChevronRight className="h-3 w-3" />
              <button
                onClick={() => setPath(crumbs.slice(0, i + 1).join('/'))}
                className="hover:text-violet-600"
              >
                {c}
              </button>
            </span>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-sm">Loading files…</span>
            </div>
          ) : (
            <div className="space-y-1">
              {(listing?.folders ?? []).map((folder) => (
                <button
                  key={folder.path}
                  onClick={() => setPath(folder.path)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 text-left"
                >
                  <Folder className="h-4 w-4 text-violet-400 flex-shrink-0" />
                  <span className="truncate">{folder.name}</span>
                </button>
              ))}
              {(listing?.files ?? []).map((file) => {
                const isSelected = selected[file.path] !== undefined
                return (
                  <div
                    key={file.path}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${isSelected ? 'bg-violet-50' : 'hover:bg-gray-50'}`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleFile(file)}
                      className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-400"
                    />
                    <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="flex-1 truncate text-gray-700">{file.name}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{formatBytes(file.size)}</span>
                    {isSelected && (
                      <select
                        value={selected[file.path]}
                        onChange={(e) =>
                          setSelected((prev) => ({ ...prev, [file.path]: e.target.value }))
                        }
                        className="text-xs border border-gray-200 rounded-md px-1.5 py-1 bg-white text-gray-600 focus:outline-none focus:ring-1 focus:ring-violet-400"
                      >
                        {INPUT_CATEGORIES.map((c) => (
                          <option key={c.key} value={c.key}>{c.label}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )
              })}
              {(listing?.folders?.length ?? 0) === 0 && (listing?.files?.length ?? 0) === 0 && (
                <p className="text-sm text-gray-400 text-center py-10">No files in this folder</p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <p className="text-xs text-gray-500">{selectedCount} file{selectedCount === 1 ? '' : 's'} selected</p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              disabled={selectedCount === 0 || isSyncing}
              onClick={() =>
                onSync(
                  Object.entries(selected).map(([source, category]) => ({
                    source,
                    category,
                    // Stage 1 loads some categories from a fixed filename (e.g. audit).
                    ...(REQUIRED_INPUT_FILENAME[category] ? { dest_name: REQUIRED_INPUT_FILENAME[category] } : {}),
                  })),
                )
              }
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderSync className="h-4 w-4" />}
              Sync {selectedCount > 0 ? `${selectedCount} file${selectedCount === 1 ? '' : 's'}` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Sdl() {
  const queryClient = useQueryClient()
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const uploadCategoryRef = useRef<string>('crawl')

  // Lazy-init from localStorage (see Lars.tsx for the React 18 double-mount rationale).
  const [activeJobs, setActiveJobs] = useState<Record<string, ActiveJob>>(() => {
    try {
      const stored = localStorage.getItem('sdl_active_jobs')
      if (!stored) return {}
      const parsed = JSON.parse(stored)
      return typeof parsed === 'object' && parsed !== null ? parsed : {}
    } catch {
      return {}
    }
  })

  const { data: tenants = [], isLoading: tenantsLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: apiClient.getTenants,
  })

  const { data: sdlData, isLoading: sdlLoading, isFetching: sdlFetching, refetch: refetchRuns } = useQuery<SdlRunsListResponse>({
    queryKey: ['sdl-runs'],
    queryFn: async () => {
      const data = await apiClient.getSdlRuns({ summary: true })
      writeSdlRunsCache(data)
      return data
    },
    retry: false,
    staleTime: SDL_STALE_MS,
    cacheTime: 30 * 60 * 1000,
    initialData: readSdlRunsCache,
    keepPreviousData: true,
    refetchInterval: Object.keys(activeJobs).length > 0 ? POLL_INTERVAL_MS : false,
  })

  const [search, setSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(
    () => sessionStorage.getItem('sdl_tenant_id'),
  )
  type SdlView = 'grid' | 'template-settings' | 'run-detail' | 'run-settings'
  const [view, setView] = useState<SdlView>(
    () => (sessionStorage.getItem('sdl_view') as SdlView) ?? 'grid',
  )
  type SdlLayout = 'list' | 'grid'
  const [layout, setLayout] = useState<SdlLayout>(
    () => (sessionStorage.getItem('sdl_layout') as SdlLayout) ?? 'list',
  )

  const [templateSaveSuccess, setTemplateSaveSuccess] = useState(false)
  const [templateSaveError, setTemplateSaveError] = useState<string | null>(null)
  const [runConfigSaveSuccess, setRunConfigSaveSuccess] = useState(false)
  const [runConfigSaveError, setRunConfigSaveError] = useState<string | null>(null)

  const PAGE_SIZE = 9
  const [stageResults, setStageResults] = useState<Record<string, StageResultCache>>({})
  const [stageErrors, setStageErrors] = useState<Record<string, Record<number, string>>>({})
  const [actionError, setActionError] = useState<string | null>(null)
  const [actionNotice, setActionNotice] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showSotModal, setShowSotModal] = useState(false)
  const [allowOutOfOrder, setAllowOutOfOrder] = useState(false)

  const runsMap: Record<string, SdlRun> = {}
  sdlData?.runs.forEach((r) => { runsMap[r.run_name] = r })

  const selectedTenant = tenants.find((t) => t.id === selectedTenantId) ?? null
  const summaryRun = selectedTenant ? runsMap[selectedTenant.subdomain] ?? null : null

  const { data: runDetail } = useQuery({
    queryKey: ['sdl-run', selectedTenant?.subdomain],
    queryFn: () => apiClient.getSdlRun(selectedTenant!.subdomain),
    enabled: !!selectedTenant && !!summaryRun,
    staleTime: SDL_STALE_MS,
    retry: false,
  })

  const selectedRun = runDetail ?? summaryRun
  const selectedActiveJob = selectedTenant ? activeJobs[selectedTenant.subdomain] ?? null : null

  const { data: inputsData, refetch: refetchInputs } = useQuery({
    queryKey: ['sdl-inputs', selectedRun?.run_name],
    queryFn: () => apiClient.listSdlInputs(selectedRun!.run_name),
    enabled: !!selectedRun && view === 'run-detail',
    staleTime: SDL_STALE_MS,
    retry: false,
  })

  const filteredTenants = tenants.filter((t) => {
    if (!search) return true
    const q = search.toLowerCase()
    return t.name.toLowerCase().includes(q) || t.subdomain.toLowerCase().includes(q)
  })

  useEffect(() => { setCurrentPage(1) }, [search])

  const totalPages = Math.ceil(filteredTenants.length / PAGE_SIZE)
  const pagedTenants = filteredTenants.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const showingFrom = filteredTenants.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1
  const showingTo = Math.min(currentPage * PAGE_SIZE, filteredTenants.length)

  // ── Job polling ────────────────────────────────────────────────────────────

  const pollTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const pollJob = useCallback(
    async (runName: string, jobId: string, stageNumber: number) => {
      try {
        const job: SdlJob = await apiClient.getSdlJobStatus(jobId)
        if (job.status === 'completed') {
          await refetchRuns()
          queryClient.invalidateQueries({ queryKey: ['sdl-run', runName] })
          queryClient.invalidateQueries({ queryKey: ['sdl-inputs', runName] })
          setActiveJobs((prev) => {
            const next = { ...prev }
            delete next[runName]
            return next
          })
          setStageErrors((prev) => {
            const forRun = { ...(prev[runName] ?? {}) }
            delete forRun[stageNumber]
            return { ...prev, [runName]: forRun }
          })
          try {
            const result = await apiClient.getSdlStageResult(runName, stageNumber)
            setStageResults((prev) => ({
              ...prev,
              [runName]: { ...(prev[runName] ?? {}), [stageNumber]: result },
            }))
          } catch {
            // result not critical
          }
        } else if (job.status === 'failed') {
          await refetchRuns()
          queryClient.invalidateQueries({ queryKey: ['sdl-run', runName] })
          setActiveJobs((prev) => {
            const next = { ...prev }
            delete next[runName]
            return next
          })
          setStageErrors((prev) => ({
            ...prev,
            [runName]: { ...(prev[runName] ?? {}), [stageNumber]: job.error ?? 'Unknown error' },
          }))
          setActionError(`Stage ${stageNumber} failed: ${job.error ?? 'Unknown error'}`)
        } else if (job.status === 'cancelled') {
          setActiveJobs((prev) => {
            const next = { ...prev }
            delete next[runName]
            return next
          })
          clearTimeout(pollTimers.current[runName])
        } else {
          pollTimers.current[runName] = setTimeout(
            () => pollJob(runName, jobId, stageNumber),
            POLL_INTERVAL_MS
          )
        }
      } catch {
        pollTimers.current[runName] = setTimeout(
          () => pollJob(runName, jobId, stageNumber),
          POLL_INTERVAL_MS
        )
      }
    },
    [refetchRuns, queryClient]
  )

  useEffect(() => {
    return () => { Object.values(pollTimers.current).forEach(clearTimeout) }
  }, [])

  const pollJobRef = useRef(pollJob)
  useEffect(() => { pollJobRef.current = pollJob }, [pollJob])

  // Persist activeJobs to localStorage whenever it changes.
  useEffect(() => {
    if (Object.keys(activeJobs).length > 0) {
      localStorage.setItem('sdl_active_jobs', JSON.stringify(activeJobs))
    } else {
      localStorage.removeItem('sdl_active_jobs')
    }
  }, [activeJobs])

  // Persist view + selectedTenantId to sessionStorage.
  useEffect(() => {
    sessionStorage.setItem('sdl_view', view)
    sessionStorage.setItem('sdl_layout', layout)
    if (selectedTenantId) {
      sessionStorage.setItem('sdl_tenant_id', selectedTenantId)
    } else {
      sessionStorage.removeItem('sdl_tenant_id')
    }
  }, [view, layout, selectedTenantId])

  // On mount, restart job polling for restored active jobs.
  useEffect(() => {
    Object.entries(activeJobs).forEach(([runName, job]) => {
      clearTimeout(pollTimers.current[runName])
      pollTimers.current[runName] = setTimeout(
        () => pollJobRef.current(runName, job.jobId, job.stageNumber),
        POLL_INTERVAL_MS,
      )
    })
  }, []) // intentionally mount-only

  // Reconcile: clear activeJobs whose run was deleted or whose stage completed.
  useEffect(() => {
    if (!sdlData) return
    const map: Record<string, SdlRun> = {}
    sdlData.runs.forEach((r) => { map[r.run_name] = r })
    setActiveJobs((prev) => {
      const next = { ...prev }
      let changed = false
      for (const [runName, job] of Object.entries(next)) {
        const run = map[runName]
        if (!run) {
          delete next[runName]
          clearTimeout(pollTimers.current[runName])
          delete pollTimers.current[runName]
          changed = true
          continue
        }
        const stage = run.stages.find((s) => s.stage_number === job.stageNumber)
        if (stage?.completed) {
          delete next[runName]
          clearTimeout(pollTimers.current[runName])
          delete pollTimers.current[runName]
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [sdlData])

  // Fetch stage results for completed stages of the selected run.
  useEffect(() => {
    if (!selectedRun) return
    const runName = selectedRun.run_name
    if (stageResults[runName]) return
    selectedRun.stages.forEach(async (s) => {
      if (s.completed) {
        try {
          const result = await apiClient.getSdlStageResult(runName, s.stage_number)
          setStageResults((prev) => ({
            ...prev,
            [runName]: { ...(prev[runName] ?? {}), [s.stage_number]: result },
          }))
        } catch {
          // stage result not available yet
        }
      }
    })
  }, [selectedRun?.run_name, selectedRun?.completed_count])

  // ── Config queries ─────────────────────────────────────────────────────────

  const { data: templateConfig, isLoading: templateConfigLoading } = useQuery({
    queryKey: ['sdl-template-config'],
    queryFn: apiClient.getSdlTemplateConfig,
    enabled: view === 'template-settings',
    staleTime: SDL_STALE_MS,
    retry: false,
  })

  const { data: runConfig, isLoading: runConfigLoading } = useQuery({
    queryKey: ['sdl-run-config', selectedRun?.run_name],
    queryFn: () => apiClient.getSdlRunConfig(selectedRun!.run_name),
    enabled: view === 'run-settings' && !!selectedRun,
    staleTime: SDL_STALE_MS,
    retry: false,
  })

  // ── Mutations ──────────────────────────────────────────────────────────────

  const createRunMutation = useMutation({
    mutationFn: ({ subdomain, tenantId, name }: { subdomain: string; tenantId: string; name: string }) =>
      apiClient.createSdlRun({
        run_name: subdomain,
        config_overrides: {
          run_name: subdomain,
          tenant_id: tenantId,
          subdomain,
          company_name: name,
        },
      }),
    onSuccess: () => {
      clearSdlRunsCache()
      refetchRuns()
    },
    onError: (e: any) => setActionError(e.message ?? 'Failed to create run'),
  })

  const deleteRunMutation = useMutation({
    mutationFn: (runName: string) => apiClient.deleteSdlRun(runName),
    onSuccess: (_d, runName) => {
      clearSdlRunsCache()
      setShowDeleteModal(false)
      setActiveJobs((prev) => {
        const next = { ...prev }
        delete next[runName]
        return next
      })
      queryClient.removeQueries({ queryKey: ['sdl-run', runName] })
      queryClient.removeQueries({ queryKey: ['sdl-inputs', runName] })
      refetchRuns()
    },
    onError: (e: any) => setActionError(e.message ?? 'Failed to delete run'),
  })

  const runStageMutation = useMutation({
    mutationFn: ({ runName, stage, variant }: { runName: string; stage: number; variant?: string }) =>
      apiClient.runSdlStage(runName, stage, variant),
    onSuccess: (data, { runName, stage }) => {
      setActionError(null)
      setActiveJobs((prev) => ({
        ...prev,
        [runName]: { jobId: data.job_id, stageNumber: stage },
      }))
      pollTimers.current[runName] = setTimeout(
        () => pollJobRef.current(runName, data.job_id, stage),
        POLL_INTERVAL_MS,
      )
    },
    onError: (e: any) => setActionError(e.message ?? 'Failed to start stage'),
  })

  const cancelJobMutation = useMutation({
    mutationFn: (jobId: string) => apiClient.cancelSdlJob(jobId),
    onSuccess: () => {
      refetchRuns()
    },
    onError: (e: any) => setActionError(e.message ?? 'Failed to cancel job'),
  })

  const sotSyncMutation = useMutation({
    mutationFn: ({ runName, subdomain, files }: { runName: string; subdomain: string; files: SdlSotSyncFile[] }) =>
      apiClient.syncSotToSdlRun(runName, { subdomain, files }),
    onSuccess: (data) => {
      setShowSotModal(false)
      refetchInputs()
      queryClient.invalidateQueries({ queryKey: ['sdl-run', data.run_name] })
      if (data.total_failed > 0) {
        setActionError(`SOT sync: ${data.total_copied} copied, ${data.total_failed} failed (${data.failed.map((f) => f.source).join(', ')})`)
      } else {
        setActionNotice(`SOT sync complete — ${data.total_copied} file${data.total_copied === 1 ? '' : 's'} copied into run inputs.`)
      }
    },
    onError: (e: any) => setActionError(e.message ?? 'SOT sync failed'),
  })

  const uploadInputsMutation = useMutation({
    mutationFn: ({ runName, category, files }: { runName: string; category: string; files: File[] }) =>
      apiClient.uploadSdlInputs(
        runName,
        category,
        files,
        REQUIRED_INPUT_FILENAME[category] && files.length === 1 ? REQUIRED_INPUT_FILENAME[category] : undefined,
      ),
    onSuccess: (data) => {
      refetchInputs()
      if (data.errors?.length) {
        setActionError(`Upload finished with errors: ${data.errors.join('; ')}`)
      } else {
        setActionNotice(`Uploaded ${data.uploaded.length} file${data.uploaded.length === 1 ? '' : 's'}.`)
      }
    },
    onError: (e: any) => setActionError(e.message ?? 'Upload failed'),
  })

  const deleteInputMutation = useMutation({
    mutationFn: ({ runName, path }: { runName: string; path: string }) =>
      apiClient.deleteSdlInput(runName, path),
    onSuccess: () => refetchInputs(),
    onError: (e: any) => setActionError(e.message ?? 'Failed to delete input file'),
  })

  const updateTemplateConfigMutation = useMutation({
    mutationFn: (config: Record<string, any>) => apiClient.updateSdlTemplateConfig(config),
    onSuccess: () => {
      setTemplateSaveSuccess(true)
      setTemplateSaveError(null)
      queryClient.invalidateQueries({ queryKey: ['sdl-template-config'] })
    },
    onError: (e: any) => {
      setTemplateSaveSuccess(false)
      setTemplateSaveError(e.message ?? 'Failed to save template config')
    },
  })

  const updateRunConfigMutation = useMutation({
    mutationFn: ({ runName, config }: { runName: string; config: Record<string, any> }) =>
      apiClient.updateSdlRunConfig(runName, config),
    onSuccess: (_d, { runName }) => {
      setRunConfigSaveSuccess(true)
      setRunConfigSaveError(null)
      queryClient.invalidateQueries({ queryKey: ['sdl-run-config', runName] })
    },
    onError: (e: any) => {
      setRunConfigSaveSuccess(false)
      setRunConfigSaveError(e.message ?? 'Failed to save run config')
    },
  })

  // Import to tenant: SDL questions → bulk-import webhook; entity model → entity-model upload.
  const importMutation = useMutation({
    mutationFn: async ({ runName, tenantId }: { runName: string; tenantId: string }) => {
      const sdlJson = await apiClient.getSdlOutputJson(runName, `output/${runName}_sdl.json`)
      const importResult = await apiClient.importQuestionsToEntityModel(tenantId, sdlJson)
      let modelUploaded = false
      try {
        const modelJson = await apiClient.getSdlOutputJson(runName, `output/${runName}_entity_model.json`)
        await apiClient.uploadEntityModel(tenantId, modelJson)
        modelUploaded = true
      } catch {
        // entity model artifact missing or upload failed — questions were still imported
      }
      return { importResult, modelUploaded }
    },
    onSuccess: ({ importResult, modelUploaded }) => {
      setShowImportModal(false)
      setActionNotice(
        `Imported to tenant — ${importResult.total_created} questions created, ${importResult.total_skipped} skipped` +
        (importResult.conflicts.length ? `, ${importResult.conflicts.length} conflicts` : '') +
        (modelUploaded ? '. Entity model uploaded.' : '. Entity model upload skipped.'),
      )
    },
    onError: (e: any) => {
      setShowImportModal(false)
      setActionError(e.message ?? 'Import to tenant failed')
    },
  })

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleRunStage = (stage: number, variant?: string) => {
    if (!selectedRun) return
    setActionNotice(null)
    runStageMutation.mutate({ runName: selectedRun.run_name, stage, variant })
  }

  const handleCancelJob = () => {
    if (!selectedTenant) return
    const job = activeJobs[selectedTenant.subdomain]
    if (job) cancelJobMutation.mutate(job.jobId)
  }

  const handleUploadFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedRun || !e.target.files?.length) return
    const files = Array.from(e.target.files)
    uploadInputsMutation.mutate({ runName: selectedRun.run_name, category: uploadCategoryRef.current, files })
    e.target.value = ''
  }

  const backToGrid = () => {
    setSelectedTenantId(null)
    setView('grid')
    setActionError(null)
    setActionNotice(null)
  }

  const getRunStatusForTenant = (subdomain: string): 'none' | 'running' | 'partial' | 'complete' => {
    if (activeJobs[subdomain]) return 'running'
    const run = runsMap[subdomain]
    if (!run) return 'none'
    if (run.completed_count === TOTAL_STAGES) return 'complete'
    if (run.completed_count > 0) return 'partial'
    return 'none'
  }

  const runStatusBadge = (status: 'none' | 'running' | 'partial' | 'complete') => {
    if (status === 'running') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 text-violet-700">
          <Loader2 className="h-3 w-3 animate-spin" /> Running
        </span>
      )
    }
    if (status === 'complete') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
          <CheckCircle2 className="h-3 w-3" /> Complete
        </span>
      )
    }
    if (status === 'partial') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
          <Clock className="h-3 w-3" /> In progress
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
        No run
      </span>
    )
  }

  // ─── View: template settings ────────────────────────────────────────────────

  if (view === 'template-settings') {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => setView('grid')}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-1.5">
            <span className="text-xl font-bold text-gray-900">SDL</span>
            <ChevronRight className="h-4 w-4 text-gray-400 mt-0.5" />
            <span className="text-xl font-bold text-gray-900">Template Settings</span>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-5 -mt-3">
          Defaults applied to every new SDL run. API keys are provided via service environment variables and never stored here.
        </p>
        <LarsConfigEditor
          config={templateConfig ?? null}
          isLoading={templateConfigLoading}
          isSaving={updateTemplateConfigMutation.isLoading}
          saveError={templateSaveError}
          saveSuccess={templateSaveSuccess}
          onSave={(config) => updateTemplateConfigMutation.mutate(config)}
          onDismissSuccess={() => setTemplateSaveSuccess(false)}
          onDismissError={() => setTemplateSaveError(null)}
        />
      </div>
    )
  }

  // ─── View: run settings ─────────────────────────────────────────────────────

  if (view === 'run-settings' && selectedTenant) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => setView('run-detail')}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-1.5">
            <span className="text-xl font-bold text-gray-900">{selectedTenant.name}</span>
            <ChevronRight className="h-4 w-4 text-gray-400 mt-0.5" />
            <span className="text-xl font-bold text-gray-900">Run Settings</span>
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-5 -mt-3">
          Config for this run only — <span className="font-medium">tenant_id</span>, <span className="font-medium">industry_type</span>, stage-1 input modes and stage-2 <span className="font-medium">include_sources</span> live here.
        </p>
        <LarsConfigEditor
          config={runConfig ?? null}
          isLoading={runConfigLoading}
          isSaving={updateRunConfigMutation.isLoading}
          saveError={runConfigSaveError}
          saveSuccess={runConfigSaveSuccess}
          onSave={(config) => selectedRun && updateRunConfigMutation.mutate({ runName: selectedRun.run_name, config })}
          onDismissSuccess={() => setRunConfigSaveSuccess(false)}
          onDismissError={() => setRunConfigSaveError(null)}
        />
      </div>
    )
  }

  // ─── View: run detail ───────────────────────────────────────────────────────

  if (selectedTenant && view !== 'grid') {
    const run = selectedRun
    const runName = selectedTenant.subdomain
    const results = run ? stageResults[run.run_name] ?? {} : {}
    const errorsForRun = stageErrors[runName] ?? {}
    const outputs = run?.outputs ?? []
    // Artifact names are run-relative paths, e.g. "output/<run>_sdl.json".
    const sdlArtifact = outputs.find((o) => o.name === `output/${runName}_sdl.json`)
    const canImport = !!sdlArtifact?.exists

    return (
      <div>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <button
              onClick={backToGrid}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-sm font-medium text-gray-900">
              <span className="font-mono">{selectedTenant.subdomain}</span>
              <span className="ml-1.5 font-normal text-gray-400">({selectedTenant.name})</span>
            </span>
          </div>
          {run && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setView('run-settings')}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-violet-300 transition-colors"
              >
                <Settings2 className="h-4 w-4" />
                Run Settings
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-red-200 bg-white text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Delete Run
              </button>
            </div>
          )}
        </div>

        {actionError && (
          <div className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="flex-1">{actionError}</span>
            <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600 font-medium">✕</button>
          </div>
        )}
        {actionNotice && (
          <div className="mb-5 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="flex-1">{actionNotice}</span>
            <button onClick={() => setActionNotice(null)} className="text-emerald-400 hover:text-emerald-600 font-medium">✕</button>
          </div>
        )}

        {!run ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white py-20">
            <FlaskConical className="h-10 w-10 text-gray-300 mb-3" />
            <p className="text-sm text-gray-500 mb-4">No SDL run for this tenant yet</p>
            <button
              onClick={() =>
                createRunMutation.mutate({
                  subdomain: selectedTenant.subdomain,
                  tenantId: selectedTenant.id,
                  name: selectedTenant.name,
                })
              }
              disabled={createRunMutation.isLoading}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-40 transition-colors"
            >
              {createRunMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create SDL Run
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* ── Inputs card ─────────────────────────────────────────────── */}
            <div className="rounded-2xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Run Inputs</h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {inputsData ? `${inputsData.total_files} file${inputsData.total_files === 1 ? '' : 's'} across ${Object.keys(inputsData.categories).length} categories` : 'Crawl, competitor and questionnaire material feeding the pipeline'}
                  </p>
                </div>
                <button
                  onClick={() => setShowSotModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                >
                  <FolderSync className="h-4 w-4" />
                  Sync from SOT
                </button>
              </div>
              <div className="divide-y divide-gray-50">
                {INPUT_CATEGORIES.map((cat) => {
                  const catData = inputsData?.categories?.[cat.key]
                  const files = catData?.files ?? []
                  return (
                    <div key={cat.key} className="px-6 py-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-700">
                            {cat.label}
                            <span className="ml-2 font-normal text-gray-400">{files.length} file{files.length === 1 ? '' : 's'}</span>
                          </p>
                          <p className="mt-0.5 text-[11px] leading-snug text-gray-400">{cat.hint}</p>
                        </div>
                        <button
                          onClick={() => {
                            uploadCategoryRef.current = cat.key
                            if (uploadInputRef.current) {
                              uploadInputRef.current.accept = cat.accept ?? ''
                              uploadInputRef.current.click()
                            }
                          }}
                          disabled={uploadInputsMutation.isLoading}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-violet-600 disabled:opacity-40 transition-colors flex-shrink-0"
                          title={cat.hint}
                        >
                          <Upload className="h-3 w-3" />
                          Upload
                        </button>
                      </div>
                      {files.length > 0 && (
                        <div className="mt-2 space-y-0.5">
                          {files.map((f) => (
                            <div key={f.path} className="flex items-center gap-2 text-xs text-gray-500 group">
                              <FileText className="h-3 w-3 text-gray-300 flex-shrink-0" />
                              <span className="flex-1 truncate">{f.name}</span>
                              <span className="text-gray-300">{formatBytes(f.size)}</span>
                              <button
                                onClick={() => deleteInputMutation.mutate({ runName: run.run_name, path: f.path })}
                                className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all"
                                title="Delete file"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              <input
                ref={uploadInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleUploadFiles}
              />
            </div>

            {/* ── Pipeline card ────────────────────────────────────────────── */}
            <div className="rounded-2xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Generation Pipeline</h2>
                  <p className="text-xs text-gray-500 mt-0.5">{run.completed_count}/{TOTAL_STAGES} stages complete</p>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer" title="Stages normally run in order — enable to run any stage regardless of earlier stages">
                  <input
                    type="checkbox"
                    checked={allowOutOfOrder}
                    onChange={(e) => setAllowOutOfOrder(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-violet-600 focus:ring-violet-400"
                  />
                  Allow out-of-order runs
                </label>
              </div>
              <div className="p-6 space-y-3">
                {Array.from({ length: TOTAL_STAGES }, (_, i) => i + 1).map((stage) => {
                  const stageStatus = run.stages.find((s) => s.stage_number === stage)
                  const prevComplete =
                    stage === 1 || (run.stages.find((s) => s.stage_number === stage - 1)?.completed ?? false)
                  const blocked = !prevComplete && !allowOutOfOrder
                  return (
                    <StageCard
                      key={stage}
                      stageNumber={stage}
                      stageStatus={stageStatus}
                      activeJob={selectedActiveJob}
                      result={results[stage] ?? null}
                      onRun={handleRunStage}
                      onCancel={handleCancelJob}
                      onDownloadLog={(s) => apiClient.downloadSdlStageLog(run.run_name, s).catch((e) => setActionError(e.message))}
                      runLoading={runStageMutation.isLoading || !!selectedActiveJob}
                      cancelLoading={cancelJobMutation.isLoading}
                      disableRun={blocked}
                      disableRunTitle={blocked ? `Stage ${stage - 1} has not completed yet` : undefined}
                      lastError={errorsForRun[stage] ?? null}
                    />
                  )
                })}
              </div>
            </div>

            {/* ── Outputs card ─────────────────────────────────────────────── */}
            <div className="rounded-2xl border border-gray-200 bg-white">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Outputs</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Final artifacts produced by stage 7</p>
                </div>
                <button
                  onClick={() => setShowImportModal(true)}
                  disabled={!canImport || importMutation.isLoading}
                  title={canImport ? 'Import the generated SDL and entity model into the tenant' : 'Run stage 7 first to produce the SDL'}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {importMutation.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Import className="h-4 w-4" />}
                  Import to Tenant
                </button>
              </div>
              <div className="divide-y divide-gray-50">
                {outputs.length === 0 ? (
                  <p className="px-6 py-6 text-sm text-gray-400 text-center">No outputs yet — run the pipeline to generate the SDL.</p>
                ) : (
                  outputs.map((artifact) => (
                    <div key={artifact.name} className="flex items-center gap-3 px-6 py-3">
                      <FileJson className={`h-4 w-4 flex-shrink-0 ${artifact.exists ? 'text-violet-500' : 'text-gray-200'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${artifact.exists ? 'text-gray-800' : 'text-gray-400'}`}>
                          {artifact.name.split('/').pop()}
                        </p>
                        {artifact.exists && (
                          <p className="text-xs text-gray-400">
                            {formatBytes(artifact.size)}{artifact.last_updated ? ` · ${formatRelativeTime(artifact.last_updated)}` : ''}
                          </p>
                        )}
                      </div>
                      {artifact.exists ? (
                        <button
                          onClick={() => {
                            apiClient.downloadSdlOutput(run.run_name, artifact.name).catch((e) => setActionError(e.message))
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:text-violet-600 transition-colors"
                        >
                          <Download className="h-3.5 w-3.5" />
                          Download
                        </button>
                      ) : (
                        <span className="text-xs text-gray-300">not generated</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modals */}
        <SotSyncModal
          subdomain={selectedTenant.subdomain}
          isOpen={showSotModal}
          isSyncing={sotSyncMutation.isLoading}
          onClose={() => setShowSotModal(false)}
          onSync={(files) =>
            run && sotSyncMutation.mutate({ runName: run.run_name, subdomain: selectedTenant.subdomain, files })
          }
        />
        <ConfirmationModal
          isOpen={showDeleteModal}
          title="Delete SDL run"
          message={`Delete the SDL run for "${selectedTenant.name}"? All inputs, stage outputs and generated artifacts for this run will be removed. This cannot be undone.`}
          confirmText="Delete Run"
          type="danger"
          isLoading={deleteRunMutation.isLoading}
          onConfirm={() => { if (run) deleteRunMutation.mutate(run.run_name) }}
          onClose={() => setShowDeleteModal(false)}
        />
        <ConfirmationModal
          isOpen={showImportModal}
          title="Import SDL to tenant"
          message={`Import the generated SDL questions and entity model into "${selectedTenant.name}"? Existing questions with matching text are skipped; the entity model becomes the tenant's current version.`}
          confirmText="Import"
          type="warning"
          isLoading={importMutation.isLoading}
          onConfirm={() => { if (run) importMutation.mutate({ runName: run.run_name, tenantId: selectedTenant.id }) }}
          onClose={() => setShowImportModal(false)}
        />
      </div>
    )
  }

  // ─── View: tenant grid ──────────────────────────────────────────────────────

  const hasSdlData = !!sdlData
  const showGridSpinner = tenantsLoading || (sdlLoading && !hasSdlData)

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100">
            <FlaskConical className="h-6 w-6 text-violet-600" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="text-2xl font-bold text-gray-900">Labs</span>
              <ChevronRight className="h-5 w-5 text-gray-400 mt-0.5" />
              <span className="text-2xl font-bold text-gray-900">SDL</span>
            </div>
            <p className="text-sm text-gray-500">Supplemental Data Layer — generate the SDL from tenant source material</p>
          </div>
        </div>
        <button
          onClick={() => setView('template-settings')}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 hover:border-violet-300 transition-colors"
        >
          <Settings2 className="h-4 w-4" />
          Settings
        </button>
      </div>

      {actionError && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span className="flex-1">{actionError}</span>
          <button onClick={() => setActionError(null)} className="text-red-400 hover:text-red-600 font-medium">✕</button>
        </div>
      )}

      <div className="mb-5 flex items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search tenants…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent"
          />
        </div>
        <div className="flex items-center rounded-lg border border-gray-200 overflow-hidden flex-shrink-0">
          <button
            type="button"
            onClick={() => setLayout('list')}
            title="List view"
            className={`inline-flex items-center justify-center h-9 w-9 transition-colors ${layout === 'list'
              ? 'bg-violet-50 text-violet-600'
              : 'bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
          >
            <List className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setLayout('grid')}
            title="Grid view"
            className={`inline-flex items-center justify-center h-9 w-9 border-l border-gray-200 transition-colors ${layout === 'grid'
              ? 'bg-violet-50 text-violet-600'
              : 'bg-white text-gray-400 hover:text-gray-600 hover:bg-gray-50'
              }`}
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
        </div>
      </div>

      {sdlFetching && hasSdlData && (
        <div className="mb-4 flex items-center gap-2 text-xs text-gray-400">
          <Loader2 className="h-3 w-3 animate-spin" />
          Refreshing run status…
        </div>
      )}

      {showGridSpinner ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span className="text-sm">Loading tenants…</span>
        </div>
      ) : filteredTenants.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-20">No tenants found</p>
      ) : (
        <>
          {layout === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pagedTenants.map((tenant) => {
                const status = getRunStatusForTenant(tenant.subdomain)
                const run = runsMap[tenant.subdomain]
                return (
                  <button
                    key={tenant.id}
                    onClick={() => {
                      setSelectedTenantId(tenant.id)
                      setView('run-detail')
                      setActionError(null)
                      setActionNotice(null)
                    }}
                    className="group text-left rounded-2xl border border-gray-200 bg-white px-5 py-4 hover:border-violet-300 hover:shadow-sm transition-all duration-150"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 flex-shrink-0">
                        <FlaskConical className="h-4 w-4 text-violet-500" />
                      </div>
                      {runStatusBadge(status)}
                    </div>
                    <p className="text-sm font-semibold text-gray-900 truncate">{tenant.name}</p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{tenant.subdomain}</p>
                    {run ? (
                      <div className="mt-3">
                        <RunProgressBar completed={run.completed_count} total={TOTAL_STAGES} />
                        <p className="text-xs text-gray-400 mt-1">{run.completed_count}/{TOTAL_STAGES} stages complete</p>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 mt-3">No run created yet</p>
                    )}
                  </button>
                )
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-200 bg-white divide-y divide-gray-50 overflow-hidden">
              {pagedTenants.map((tenant) => {
                const status = getRunStatusForTenant(tenant.subdomain)
                const run = runsMap[tenant.subdomain]
                return (
                  <button
                    key={tenant.id}
                    onClick={() => {
                      setSelectedTenantId(tenant.id)
                      setView('run-detail')
                      setActionError(null)
                      setActionNotice(null)
                    }}
                    className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50/70 transition-colors text-left"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-violet-50 flex-shrink-0">
                      <FlaskConical className="h-4 w-4 text-violet-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{tenant.name}</p>
                      <p className="text-xs text-gray-400 truncate">{tenant.subdomain}</p>
                    </div>
                    <div className="w-44 flex-shrink-0 hidden sm:block">
                      {run ? (
                        <>
                          <RunProgressBar completed={run.completed_count} total={TOTAL_STAGES} />
                          <p className="text-xs text-gray-400 mt-1">{run.completed_count}/{TOTAL_STAGES} stages</p>
                        </>
                      ) : (
                        <p className="text-xs text-gray-400">No run yet</p>
                      )}
                    </div>
                    <div className="flex-shrink-0">{runStatusBadge(status)}</div>
                    <ChevronRight className="h-4 w-4 text-gray-300 flex-shrink-0" />
                  </button>
                )
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-5 flex items-center justify-between text-sm text-gray-500">
              <p className="text-xs">
                Showing {showingFrom}–{showingTo} of {filteredTenants.length}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="px-2 text-xs">{currentPage} / {totalPages}</span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
